import { Trans, useTranslation } from 'react-i18next'
import { region as regionFunctions } from '@sellpy/commons'
import { gql, useQuery } from '@apollo/client'
import { A, Checkbox, PrimaryCheckboxLabel } from '@sellpy/design-system-react-web'
import { Controller } from 'react-hook-form'
import { getRegion } from '../../../common/region/region.js'
import {
  PERMISSION_TYPE_EMAIL_MARKETING,
  PERMISSION_TYPE_DATA_POLICY,
  PERMISSION_TYPE_LOYALTY_PROGRAM
} from '../../../common/user/dataHandling.js'
import { CheckoutPolicies } from '../../checkout/CheckoutPolicies.jsx'
import { useCurrentUser } from '../../../common/user/currentApolloUser.js'
import DataPolicyNotice from './DataPolicyNotice.jsx'
import { useEmailMarketingConsent, useUserDataConsent } from './index.js'

const policyLinks = {
  [PERMISSION_TYPE_DATA_POLICY]: regionFunctions.dataPolicyLink(process.env.REGION),
  [PERMISSION_TYPE_LOYALTY_PROGRAM]: regionFunctions.loyaltyPolicyLink(process.env.REGION)
}

const getConsentLabel = ({ t, permissionType, alreadyHaveNewsletterPromocode }) => {
  const link = policyLinks[permissionType]
  return link ? (
    <Trans i18nKey={`dataUsageConsent.${permissionType}`}>
      part1
      <A noMargin target='_blank' href={link} onClick={(e) => e.stopPropagation()}>
        link
      </A>
    </Trans>
  ) : permissionType === PERMISSION_TYPE_EMAIL_MARKETING ? (
    t(
      `dataUsageConsent.${PERMISSION_TYPE_EMAIL_MARKETING}${
        alreadyHaveNewsletterPromocode ? 'NoPromocode' : ''
      }`
    )
  ) : (
    t(`dataUsageConsent.${permissionType}`)
  )
}

const CLOUD_GET_EMAIL_NEWSLETTERSIGNUP = gql`
  query getEmailNewsletterSignup($email: String!) {
    newsletterSignup: getEmailNewsletterSignup(email: $email) {
      objectId
    }
  }
`

/**
 * @param {Object} props
 * @param {Array} props.permissions
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} [props.errors]
 * @param {boolean} [props.noMargin]
 */
const DataConsent = ({
  permissions,
  control,
  showCheckoutPolicies,
  noMargin,
  regionOverride = getRegion()
}) => {
  const { t } = useTranslation('common')
  const user = useCurrentUser()
  const { userDataConsents, loading: userDataConsentLoading } = useUserDataConsent()
  const { emailMarketingConsent, loading: emailMarketingConsentLoading } =
    useEmailMarketingConsent()

  const email = user?.email
  const { loading: newsletterSignupLoading, data: newsletterSignupData } = useQuery(
    CLOUD_GET_EMAIL_NEWSLETTERSIGNUP,
    {
      variables: { email },
      skip: !email
    }
  )

  if (userDataConsentLoading || emailMarketingConsentLoading || newsletterSignupLoading) return null

  const permissionsToShow = permissions
    .filter(({ type }) => !userDataConsents[type])
    .filter(({ type }) => type !== PERMISSION_TYPE_EMAIL_MARKETING || !emailMarketingConsent)
    .filter(({ type }) => type !== PERMISSION_TYPE_DATA_POLICY)
  const consentedPermissions = permissions.filter(
    ({ type }) =>
      userDataConsents[type] || (type === PERMISSION_TYPE_EMAIL_MARKETING && emailMarketingConsent)
  )

  const implicitlyAddDataPolicy =
    permissions.some(({ type }) => type === PERMISSION_TYPE_DATA_POLICY) &&
    !consentedPermissions.some(({ type }) => type === PERMISSION_TYPE_DATA_POLICY)

  return (
    <div
      style={{
        marginTop: noMargin ? '-8px' : 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
      }}
    >
      {permissionsToShow.map(({ type: permissionType, required }) => (
        <Controller
          key={permissionType}
          name={`dataUsageConsent.${permissionType}`}
          control={control}
          rules={{ required: required ? t('dataUsageConsent.required') : null }}
          render={({ field, fieldState }) => (
            <Checkbox
              name={field.name}
              checked={field.value || false}
              onChange={field.onChange}
              onBlur={field.onBlur}
              labelComponent={
                <PrimaryCheckboxLabel
                  label={getConsentLabel({
                    t,
                    permissionType,
                    alreadyHaveNewsletterPromocode: Boolean(newsletterSignupData?.newsletterSignup)
                  })}
                />
              }
              erroneous={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      ))}
      {/* We want the consented permissions to be part of the submit values
       without showing checkboxes for them */}
      {consentedPermissions.map(({ type: permissionType }) => (
        <Controller
          name={`dataUsageConsent.${permissionType}`}
          control={control}
          key={permissionType}
          defaultValue={true}
          render={() => null}
        />
      ))}
      {/* Include data policy if it's part of the permissions array, but never show it. */}
      {implicitlyAddDataPolicy && (
        <Controller
          name={`dataUsageConsent.${PERMISSION_TYPE_DATA_POLICY}`}
          control={control}
          defaultValue={true}
          render={() => null}
        />
      )}
      {showCheckoutPolicies && <CheckoutPolicies withSalePolicy region={regionOverride} />}
      {!showCheckoutPolicies && implicitlyAddDataPolicy && (
        <DataPolicyNotice region={regionOverride} noMargin={noMargin} />
      )}
    </div>
  )
}

export default DataConsent
